import React, { useState, useRef, useEffect, useMemo } from 'react';
import { TwemojiText } from '../../utils/appleEmoji';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, MessageCircle, Users, Pin, Bell, BellOff, Trash2, UserX, User, MessageSquarePlus, UserPlus, MessageSquare, Settings, Image as ImageIcon, Minus, Circle, Star, UsersRound, MoreVertical, Lock, LogOut, CheckSquare, ArrowLeft } from 'lucide-react';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import AddFriendModal from './AddFriendModal';
import FriendsListModal from './FriendsListModal';
import MuteFriendModal from './MuteFriendModal';
import NicknameModal from './NicknameModal';
import CreateGroupModal from './CreateGroupModal';
import ContextMenu from '../Common/ContextMenu';
import DeleteModal from '../Common/DeleteModal';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { avatarApi } from '../../services/cloudApi';
import AvatarCropModal from '../Common/AvatarCropModal';
import { getSocket } from '../../services/socketService';
import { formatLastSeen } from '../../utils/formatLastSeen';
import { getPlusEmailColor, isPlusMember } from '../../utils/userDisplay';
import Username, { getDisplayName } from '../Common/Username';
import GroupAvatar from './GroupAvatar';

// Helper function to get user initial from email
const getInitialFromEmail = (email) => {
  if (!email) return '?';
  return email.split('@')[0]?.[0]?.toUpperCase() || '?';
};

// Helper function to get user initials
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

const ChatSidebar = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('chat');
  const dateLocale = getDateFnsLocale(i18n.language);

  // Helper function to format time
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return t('sidebar.justNow');
      if (diffMins < 60) return t('sidebar.minutesAgo', { count: diffMins });
      if (diffHours < 24) return t('sidebar.hoursAgo', { count: diffHours });
      if (diffDays < 7) return t('sidebar.daysAgo', { count: diffDays });
      return format(date, 'd MMM', { locale: dateLocale });
    } catch (error) {
      return '';
    }
  };

  const {
    friends, 
    filteredFriends, 
    selectedFriendId, 
    searchQuery, 
    setSearchQuery, 
    selectFriend,
    pinFriend,
    muteFriend,
    setNickname,
    isFriendMuted,
    deleteMessages,
    deleteFriend,
    pendingFriendRequestCount,
    loadUnreadCount,
    // Group chat
    groups,
    selectedGroupId,
    selectGroup,
    muteGroup,
    leaveGroup,
    deleteGroup,
    destroyGroup,
    clearGroupMessages,
    deleteGroupMessages,
    removeGroupFromSidebar,
    pinGroup,
    loadGroups,
    messages,
    groupMessages,
  } = useChat();
  const { authToken, user, refreshUser, isAuthenticated, logout } = useAuth();
  const [showAddFriendModal, setShowAddFriendModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [contextMenu, setContextMenu] = useState({ isOpen: false, x: 0, y: 0, friend: null });
  const [groupContextMenu, setGroupContextMenu] = useState({ isOpen: false, x: 0, y: 0, group: null });
  const [emptyAreaContextMenu, setEmptyAreaContextMenu] = useState({ isOpen: false, x: 0, y: 0 });
  const [showMuteModal, setShowMuteModal] = useState(false);
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [showDeleteMessagesModal, setShowDeleteMessagesModal] = useState(false);
  const [showDeleteFriendModal, setShowDeleteFriendModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showDestroyGroupModal, setShowDestroyGroupModal] = useState(false);
  const [showClearMessagesModal, setShowClearMessagesModal] = useState(false);
  const [showDeleteGroupMessagesModal, setShowDeleteGroupMessagesModal] = useState(false);
  const [selectedFriendForAction, setSelectedFriendForAction] = useState(null);
  const [selectedGroupForAction, setSelectedGroupForAction] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [showAvatarCropModal, setShowAvatarCropModal] = useState(false);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const profileMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const [viewState, setViewState] = useState('chats'); // 'chats' or 'contacts'
  const [showThreeDotMenu, setShowThreeDotMenu] = useState(false);
  const [selectChatsMode, setSelectChatsMode] = useState(false);
  const [selectedChatsForDeletion, setSelectedChatsForDeletion] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFriendsListModal, setShowFriendsListModal] = useState(false);
  const threeDotMenuRef = useRef(null);

  // Grup kur butonuna tıklandığında modal aç
  const handleCreateGroup = () => {
    setShowCreateGroupModal(true);
  };

  // Grup seçildiğinde
  const handleSelectGroup = (groupId) => {
    if (groupId) {
      selectGroup(groupId);
    } else {
      selectGroup(null);
    }
  };

  // Grup context menu handlers
  const handleGroupMute = () => {
    if (selectedGroupForAction) {
      muteGroup(selectedGroupForAction.id, !selectedGroupForAction.muted);
    }
    setGroupContextMenu({ isOpen: false, x: 0, y: 0, group: null });
  };

  const handleGroupLeave = () => {
    if (selectedGroupForAction) {
      setShowDeleteGroupModal(true);
    }
    setGroupContextMenu({ isOpen: false, x: 0, y: 0, group: null });
  };

  const handleConfirmLeaveGroup = async () => {
    if (selectedGroupForAction) {
      await leaveGroup(selectedGroupForAction.id);
    }
    setShowDeleteGroupModal(false);
    setSelectedGroupForAction(null);
  };

  const handleDestroyGroup = () => {
    if (selectedGroupForAction) {
      setShowDestroyGroupModal(true);
    }
    setGroupContextMenu({ isOpen: false, x: 0, y: 0, group: null });
  };

  const handleConfirmDestroyGroup = async () => {
    if (selectedGroupForAction) {
      await destroyGroup(selectedGroupForAction.id);
    }
    setShowDestroyGroupModal(false);
    setSelectedGroupForAction(null);
  };

  const handleClearMessages = () => {
    if (selectedGroupForAction) {
      setShowClearMessagesModal(true);
    }
    setGroupContextMenu({ isOpen: false, x: 0, y: 0, group: null });
  };

  const handleConfirmClearMessages = async () => {
    if (selectedGroupForAction) {
      await clearGroupMessages(selectedGroupForAction.id);
    }
    setShowClearMessagesModal(false);
    setSelectedGroupForAction(null);
  };

  const handleDeleteMessages = () => {
    if (selectedGroupForAction) {
      setShowDeleteGroupMessagesModal(true);
    }
    setGroupContextMenu({ isOpen: false, x: 0, y: 0, group: null });
  };

  const handleConfirmDeleteMessages = async () => {
    if (selectedGroupForAction) {
      await deleteGroupMessages(selectedGroupForAction.id);
    }
    setShowDeleteGroupMessagesModal(false);
    setSelectedGroupForAction(null);
  };

  const handleRemoveGroup = async () => {
    if (selectedGroupForAction) {
      // Remove group from sidebar (persistent - won't reappear until new message)
      await removeGroupFromSidebar(selectedGroupForAction.id);
    }
    setGroupContextMenu({ isOpen: false, x: 0, y: 0, group: null });
  };

  // Combined list of friends and groups (sorted by lastMessageTime)
  const combinedChatList = useMemo(() => {
    const friendsList = (filteredFriends || []).map(friend => ({
      type: 'friend',
      id: friend.email || friend.id,
      data: friend,
      lastMessageTime: friend.lastMessageTime,
      pinned: friend.pinned,
    }));
    
    const groupsList = (groups || []).map(group => ({
      type: 'group',
      id: group.id,
      data: group,
      lastMessageTime: group.lastMessageTime || group.last_message_time || null,
      createdAt: group.createdAt || group.created_at || group.createdAt || null,
      pinned: group.pinned || false,
      pinnedAt: group.pinnedAt || group.pinned_at || null,
    }));
    
    // Filter groups by search query if needed
    let filteredGroupsList = groupsList;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredGroupsList = groupsList.filter(
        (item) =>
          (item.data.name && item.data.name.toLowerCase().includes(query)) ||
          (item.data.description && item.data.description.toLowerCase().includes(query))
      );
    }
    
    // Combine and sort by lastMessageTime (most recent first)
    const combined = [...friendsList, ...filteredGroupsList];
    
    return combined.sort((a, b) => {
      // Pinned items first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      
      // If both pinned, sort by pinned_at (newer pins first - most recently pinned at top)
      if (a.pinned && b.pinned) {
        if (a.pinnedAt && b.pinnedAt) {
          return new Date(b.pinnedAt) - new Date(a.pinnedAt); // Newer pinned first
        }
        if (a.pinnedAt) return -1; // a has pinnedAt, b doesn't - a comes first
        if (b.pinnedAt) return 1; // b has pinnedAt, a doesn't - b comes first
        // Neither has pinnedAt, fall through to lastMessageTime
      }
      
      // Then by lastMessageTime (most recent message first)
      if (a.lastMessageTime && b.lastMessageTime) {
        return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
      }
      if (a.lastMessageTime) return -1; // a has message, b doesn't - a comes first
      if (b.lastMessageTime) return 1; // b has message, a doesn't - b comes first
      
      // Fallback: sort by createdAt (newest groups first if no messages)
      // CRITICAL: New groups without messages should appear at top (after pinned)
      const aCreatedAt = a.createdAt || a.data.createdAt || a.data.created_at;
      const bCreatedAt = b.createdAt || b.data.createdAt || b.data.created_at;
      if (aCreatedAt && bCreatedAt) {
        return new Date(bCreatedAt) - new Date(aCreatedAt); // Newer groups first
      }
      if (aCreatedAt) return -1; // a has createdAt, b doesn't - a comes first
      if (bCreatedAt) return 1; // b has createdAt, a doesn't - b comes first
      
      return 0;
    });
  }, [filteredFriends, groups, searchQuery]);


  // Profil fotoğrafı değiştir
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // CRITICAL FIX: authToken kontrolü - giriş yapılmamışsa işlemi durdur
    if (!authToken || !user || !user.id) {
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'warning',
            title: t('sidebar.avatar.loginRequiredTitle'),
            message: t('sidebar.avatar.loginRequiredMessage'),
          },
        })
      );
      return;
    }

    if (!file.type.startsWith('image/')) {
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('sidebar.avatar.invalidFileTitle'),
            message: t('sidebar.avatar.invalidFileMessage'),
          },
        })
      );
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('sidebar.avatar.fileTooBigTitle'),
            message: t('sidebar.avatar.fileTooBigMessage'),
          },
        })
      );
      return;
    }

    setSelectedAvatarFile(file);
    setShowAvatarCropModal(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAvatarSave = async (croppedFile) => {
    if (!authToken) return;
    setIsUpdatingAvatar(true);
    try {
      const result = await avatarApi.upload({ token: authToken, file: croppedFile, targetType: 'user' });
      if (!result?.ok) throw new Error(result?.message || 'Upload failed');
      await refreshUser();
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'success',
            title: t('sidebar.avatar.successTitle'),
            message: '',
          },
        })
      );
    } catch (error) {
      // CRITICAL FIX: 401 hatası için özel mesaj göster
      if (error?.code === 'UNAUTHORIZED' || error?.status === 401) {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'error',
              title: t('sidebar.avatar.unauthorizedTitle'),
              message: t('sidebar.avatar.unauthorizedMessage'),
            },
          })
        );
      } else {
        // Sadece development modunda console.error göster
        if (process.env.NODE_ENV === 'development') {
          console.error('Avatar update error:', error);
        }
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'error',
              title: t('sidebar.avatar.errorTitle'),
              message: t('sidebar.avatar.errorMessage'),
            },
          })
        );
      }
    } finally {
      setIsUpdatingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };



  // Logout event'ini dinle ve profil menüsünü kapat
  // CRITICAL FIX: Logout sonrası profil fotoğrafını temizlemek için state'i force update et
  const [avatarKey, setAvatarKey] = useState(0);
  
  // user state'i değiştiğinde avatarKey'i güncelle - logout sonrası user null olursa image unmount edilir
  useEffect(() => {
    if (!user || !user.id) {
      // User null veya id yoksa avatarKey'i değiştir - image unmount edilir
      setAvatarKey(prev => prev + 1);
    } else {
      // User varsa avatarKey'i user.id ile güncelle - farklı kullanıcı için image yeniden render edilir
      setAvatarKey(user.id);
    }
  }, [user?.id]);
  
  useEffect(() => {
    const handleLogout = () => {
      // Profil menüsünü kapat
      setProfileMenuOpen(false);
      // Avatar key'ini değiştir - image element'inin yeniden render edilmesini sağla
      setAvatarKey(prev => prev + 1);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('notesplus-user-logged-out', handleLogout);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('notesplus-user-logged-out', handleLogout);
      }
    };
  }, []);

  // Profil menüsünü kapat
  useEffect(() => {
    if (!profileMenuOpen) return;

    const handleClickOutside = (e) => {
      // Profil menüsüne tıklandıysa, kapatma
      if (profileMenuRef.current && profileMenuRef.current.contains(e.target)) {
        return;
      }
      
      // Dışarı tıklandıysa, menüyü kapat
      setProfileMenuOpen(false);
    };

    // Event listener'ı geciktir ki buton tıklaması ve state update'i önce işlensin
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [profileMenuOpen]);

  // Get status border color (WhatsApp-like: only green for online, gray for offline)
  const getStatusBorderColor = (isOnline) => {
    if (isOnline === true) {
      return 'border-green-500';
    } else {
      return 'border-gray-400 dark:border-gray-600';
    }
  };

  const [chatFilter, setChatFilter] = useState('all'); // 'all', 'unread', 'groups'
  
  // Filter chat list based on selected filter
  const filteredChatList = useMemo(() => {
    if (chatFilter === 'all') return combinedChatList;
    if (chatFilter === 'unread') {
      return combinedChatList.filter(item => {
        if (item.type === 'friend') return item.data.unreadCount > 0;
        if (item.type === 'group') return item.data.unreadCount > 0;
        return false;
      });
    }
    if (chatFilter === 'groups') {
      return combinedChatList.filter(item => item.type === 'group');
    }
    return combinedChatList;
  }, [combinedChatList, chatFilter]);

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      let markedCount = 0;
      const socket = getSocket();
      
      if (!socket?.connected) {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'error',
              title: t('sidebar.markAllRead.errorTitle'),
              message: t('sidebar.markAllRead.connectionErrorMessage'),
            },
          })
        );
        return;
      }
      
      // Mark all friend messages as read
      for (const friend of friends) {
        const friendId = friend.email || friend.id;
        if (friendId && messages[friendId]) {
          const unreadMessages = messages[friendId].filter(
            msg => msg.senderId !== 'current_user' && !msg.isRead && msg.id && !msg.id.startsWith('temp_')
          );
          
          if (unreadMessages.length > 0) {
            unreadMessages.forEach(msg => {
              socket.emit('message:read', { message_id: msg.id });
              markedCount++;
            });
          }
        }
      }
      
      // Mark all group messages as read
      for (const group of groups) {
        if (group.id && groupMessages[group.id]) {
          const unreadMessages = groupMessages[group.id].filter(
            msg => msg.senderId !== user?.id && !msg.isRead && msg.id && !msg.id.startsWith('temp_')
          );
          
          if (unreadMessages.length > 0) {
            unreadMessages.forEach(msg => {
              socket.emit('group:message:read', { 
                group_id: group.id, 
                message_id: msg.id 
              });
              markedCount++;
            });
          }
        }
      }
      
      // Reload unread count to update UI
      if (loadUnreadCount) {
        setTimeout(() => {
          loadUnreadCount(true);
        }, 500);
      }
      
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'success',
            title: t('sidebar.markAllRead.successTitle'),
            message: markedCount > 0 ? t('sidebar.markAllRead.successMessage', { count: markedCount }) : t('sidebar.markAllRead.alreadyReadMessage'),
          },
        })
      );
    } catch (error) {
      console.error('Mark all as read error:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('sidebar.markAllRead.errorTitle'),
            message: t('sidebar.markAllRead.errorMessage'),
          },
        })
      );
    }
    setShowThreeDotMenu(false);
  };

  // Listen for lock application event from main process
  useEffect(() => {
    const handleLockRequest = () => {
      if (window.lockContext?.lock) {
        window.lockContext.lock();
      } else {
        navigate('/lock');
      }
    };
    
    if (window.api?.onLockRequested) {
      const unsubscribe = window.api.onLockRequested(handleLockRequest);
      return () => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    }
    
    return undefined;
  }, [navigate]);

  // Handle logout
  const handleLogout = async () => {
    if (logout) {
      await logout();
    }
    setShowThreeDotMenu(false);
  };

  // Get sorted friends list for new chat panel (alphabetical) - Only accepted friends
  // Backend already filters to only accepted friends, but we ensure deleted friends are excluded
  // CRITICAL: Force re-render when friends state changes
  // This ensures the "Kullanıcılar" list updates immediately when friends state changes
  // CRITICAL FIX: Removed friendsUpdateKey - friends prop from ChatContext is the single source of truth
  // When friend:accepted event arrives, ChatContext updates friends state, which automatically
  // triggers re-render of ChatSidebar and recalculation of sortedFriendsForNewChat

  // CRITICAL: Real-time friends list for "Kullanıcılar" section
  // This ensures the list updates immediately when friends state changes via socket events
  const sortedFriendsForNewChat = useMemo(() => {
    // Filter out deleted friends and sort alphabetically
    const filteredFriends = [...(friends || [])]
      .filter(friend => !friend.isDeleted) // Only show non-deleted friends (accepted friends)
      .sort((a, b) => {
        const nameA = getDisplayName(a).toLowerCase();
        const nameB = getDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB, i18n.language);
      });

    return filteredFriends;
  }, [friends, i18n.language]); // CRITICAL: friends prop from ChatContext is the single source of truth

  // CRITICAL FIX: Socket events are handled by ChatContext
  // When friend:accepted event arrives, ChatContext updates friends state via setFriends
  // This automatically triggers re-render of ChatSidebar and recalculation of sortedFriendsForNewChat
  // No need for duplicate socket listeners here - rely on ChatContext as single source of truth

  // Close three dot menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (threeDotMenuRef.current && !threeDotMenuRef.current.contains(event.target)) {
        setShowThreeDotMenu(false);
      }
    };

    if (showThreeDotMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showThreeDotMenu]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50 dark:bg-[#0d1424]">
      {/* Header - Title, New Chat, Manage Friends, Three Dot Menu */}
      <div className="p-4 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#0d1424]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 flex-1">
            {/* Profile Avatar Button */}
            <div className="relative flex-shrink-0" ref={profileMenuRef}>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 relative overflow-visible group border-2 ${getStatusBorderColor(user?.is_online)}`}
                title={t('sidebar.profileTitle')}
              >
                <div className="w-full h-full rounded-full flex items-center justify-center overflow-hidden">
                  {user && user.id && authToken && typeof authToken === 'string' && authToken.trim() !== '' && user.avatarUrl ? (
                    <img
                      key={`avatar-${user.id}-${avatarKey}-${user.avatarUrl?.substring(0, 20)}`}
                      src={user.avatarUrl}
                      alt={user.name || t('sidebar.profileTitle')}
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div className={`w-full h-full bg-[#2563eb] text-white flex items-center justify-center text-xs font-semibold rounded-full ${user && user.id && authToken && typeof authToken === 'string' && authToken.trim() !== '' && user.avatarUrl ? 'hidden' : ''}`}>
                    {user && user.id ? getInitials(user?.name || user?.email || '?') : '?'}
                  </div>
                </div>
              </motion.button>

              {/* Profile Menu */}
              <AnimatePresence>
                {profileMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    transition={{ duration: 0.12 }}
                    className="absolute top-full left-0 mt-2 z-[10000] min-w-[200px] rounded-xl border border-gray-200/70 bg-white/95 backdrop-blur-md shadow-2xl shadow-black/10 dark:border-white/10 dark:bg-[#0d1424]/95"
                  >
                    <div className="py-1">
                      <button
                        type="button"
                        onClick={() => {
                          fileInputRef.current?.click();
                          setProfileMenuOpen(false);
                        }}
                        disabled={isUpdatingAvatar}
                        className="flex w-full items-center gap-3 px-3.5 py-2.5 text-sm font-medium transition-colors duration-150 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-[#24354f]/80 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ImageIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" strokeWidth={2.2} />
                        <span className="flex-1 text-left">
                          {isUpdatingAvatar ? t('sidebar.profileMenu.updating') : t('sidebar.profileMenu.changePhoto')}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          window.dispatchEvent(
                            new CustomEvent('notesplus-show-toast', {
                              detail: {
                                type: 'info',
                                title: t('sidebar.profileMenu.settingsComingTitle'),
                                message: t('sidebar.profileMenu.settingsComingMessage'),
                              },
                            })
                          );
                        }}
                        className="flex w-full items-center gap-3 px-3.5 py-2.5 text-sm font-medium transition-colors duration-150 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-[#24354f]/80"
                      >
                        <Settings className="h-4 w-4 text-gray-500 dark:text-gray-400" strokeWidth={2.2} />
                        <span className="flex-1 text-left">{t('sidebar.profileMenu.settings')}</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>
            
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('sidebar.title')}</h2>
          </div>

          <div className="flex items-center gap-2">
            {/* New Chat Button */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setViewState('contacts')}
              className="p-2.5 rounded-xl bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-700 dark:text-gray-100 transition-colors"
              title={t('sidebar.newChatTitle')}
              aria-label={t('sidebar.newChatTitle')}
            >
              <MessageSquare className="w-4 h-4" />
            </motion.button>

            {/* Manage Friends Button */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowFriendsListModal(true)}
              className="relative p-2.5 rounded-xl bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-700 dark:text-gray-100 transition-colors"
              title={t('sidebar.menu.manageFriends')}
              aria-label={t('sidebar.menu.manageFriends')}
            >
              <Users className="w-4 h-4" />
              {pendingFriendRequestCount > 0 && (
                <span
                  className="absolute top-1.5 end-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-gray-50 dark:ring-[#0d1424]"
                  aria-hidden="true"
                />
              )}
            </motion.button>

            {/* Three Dot Menu Button */}
            <div className="relative" ref={threeDotMenuRef}>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setShowThreeDotMenu(!showThreeDotMenu)}
                className="p-2.5 rounded-xl bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-700 dark:text-gray-100 transition-colors"
                title={t('sidebar.menuTitle')}
              >
                <MoreVertical className="w-4 h-4" />
              </motion.button>
              
              {/* Three Dot Menu Dropdown */}
              <AnimatePresence>
                {showThreeDotMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    transition={{ duration: 0.12 }}
                    className="absolute top-full right-0 mt-2 z-[10000] min-w-[220px] rounded-xl border border-gray-200/70 bg-white/95 backdrop-blur-md shadow-2xl shadow-black/10 dark:border-white/10 dark:bg-[#0d1424]/95"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowCreateGroupModal(true);
                          setShowThreeDotMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#24354f]/80 transition-colors text-left"
                      >
                        <Users className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span>{t('sidebar.menu.newGroup')}</span>
                      </button>
                      <button
                        onClick={() => {
                          // Show starred messages (placeholder - can open a modal or navigate)
                          window.dispatchEvent(
                            new CustomEvent('notesplus-show-toast', {
                              detail: {
                                type: 'info',
                                title: t('sidebar.menu.starredComingTitle'),
                                message: t('sidebar.menu.starredComingMessage'),
                              },
                            })
                          );
                          setShowThreeDotMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#24354f]/80 transition-colors text-left"
                      >
                        <Star className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span>{t('sidebar.menu.starredMessages')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectChatsMode(true);
                          setShowThreeDotMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#24354f]/80 transition-colors text-left"
                      >
                        <CheckSquare className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span>{t('sidebar.menu.selectChats')}</span>
                      </button>
                      <button
                        onClick={handleMarkAllAsRead}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#24354f]/80 transition-colors text-left"
                      >
                        <Circle className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span>{t('sidebar.menu.markAllRead')}</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('sidebar.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-[#060c18] border border-gray-200 dark:border-white/5 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50/60 focus:border-[#3d8ef0]/50/60 transition-all"
          />
        </div>
        
        {/* Filter Pills */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setChatFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              chatFilter === 'all'
                ? 'bg-[#2563eb] text-white'
                : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-white/10'
            }`}
          >
            {t('sidebar.filters.all')}
          </button>
          <button
            onClick={() => setChatFilter('unread')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              chatFilter === 'unread'
                ? 'bg-[#2563eb] text-white'
                : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-white/10'
            }`}
          >
            {t('sidebar.filters.unread')}
          </button>
          <button
            onClick={() => setChatFilter('groups')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              chatFilter === 'groups'
                ? 'bg-[#2563eb] text-white'
                : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-white/10'
            }`}
          >
            {t('sidebar.filters.groups')}
          </button>
        </div>
      </div>


      {/* Content Area - Contacts Panel or Chat List */}
      <div className="chat-scroll-area flex-1 overflow-y-auto min-h-0 relative">
        <AnimatePresence mode="wait">
          {viewState === 'contacts' ? (
            <motion.div
              key="contacts"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col h-full absolute inset-0"
            >
              {/* Back Button */}
              <div className="p-4 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#0d1424]">
                <button
                  onClick={() => setViewState('chats')}
                  className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">{t('sidebar.back')}</span>
                </button>
              </div>

              {/* Quick Actions */}
              <div className="p-4 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#0d1424] space-y-2">
                <button
                  onClick={() => {
                    setShowCreateGroupModal(true);
                    setViewState('chats');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-100 dark:bg-[#060c18] hover:bg-slate-200 dark:hover:bg-[#0d1424] text-gray-900 dark:text-white transition-colors text-left"
                >
                  <Users className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <span className="font-medium">{t('sidebar.quickActions.newGroup')}</span>
                </button>
                <button
                  onClick={() => {
                    setShowAddFriendModal(true);
                    setViewState('chats');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-100 dark:bg-[#060c18] hover:bg-slate-200 dark:hover:bg-[#0d1424] text-gray-900 dark:text-white transition-colors text-left"
                >
                  <UserPlus className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <span className="font-medium">{t('sidebar.quickActions.addFriend')}</span>
                </button>
              </div>

              {/* User List - Alphabetically Sorted (Only Accepted Friends) */}
              <div className="chat-scroll-area flex-1 overflow-y-auto p-2 bg-gray-50 dark:bg-[#0d1424]">
                <div className="px-2 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t('sidebar.usersLabel')}
                </div>
                {sortedFriendsForNewChat.length === 0 ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-12">
                    <p>{t('sidebar.noFriendsYet')}</p>
                    <p className="text-xs mt-1">{t('sidebar.noFriendsHint')}</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {sortedFriendsForNewChat.map((friend) => {
                      const friendId = friend.email || friend.id;
                      // CRITICAL: Check multiple sources for online status
                      const isOnline = friend.status === 'online' || friend.is_online === true || friend.statusDot === 'green';
                      // CRITICAL: Get last_seen from multiple sources
                      const lastSeen = friend.last_seen || friend.lastSeen || null;
                      // CRITICAL: Get statusText for display
                      const statusText = friend.statusText || (isOnline ? t('sidebar.online') : (lastSeen ? formatLastSeen(lastSeen) : ''));
                      
                      return (
                        <motion.button
                          key={friendId} // CRITICAL: friendId is unique, no need for update key
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => {
                            selectFriend(friendId);
                            setViewState('chats');
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#24354f]/50 transition-colors text-left"
                        >
                          <div className="relative flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 dark:bg-[#2563eb]/10 flex items-center justify-center text-sm font-semibold text-[#3d8ef0] dark:text-[#7ab8f8]">
                              {friend.avatarUrl ? (
                                <img
                                  src={friend.avatarUrl}
                                  alt={getDisplayName(friend)}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                getInitials(getDisplayName(friend))
                              )}
                            </div>
                            {isOnline && (
                              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-[#0d1424]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">
                              <Username user={friend} variant="default" />
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {statusText || (isOnline ? t('sidebar.online') : '')}
                            </p>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          ) : selectChatsMode ? (
            /* Select Chats Mode */
            <motion.div
              key="selectChats"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col h-full absolute inset-0"
            >
              <div className="p-4 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-[#0d1424] flex items-center justify-between">
                <button
                  onClick={() => {
                    setSelectChatsMode(false);
                    setSelectedChatsForDeletion(new Set());
                  }}
                  className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">{t('sidebar.selectChats.cancel')}</span>
                </button>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('sidebar.selectChats.selectedCount', { count: selectedChatsForDeletion.size })}
                </span>
                <button
                  onClick={() => {
                    if (selectedChatsForDeletion.size > 0) {
                      setShowDeleteConfirm(true);
                    }
                  }}
                  disabled={selectedChatsForDeletion.size === 0}
                  className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('sidebar.selectChats.delete')}
                </button>
              </div>
              <div className="chat-scroll-area flex-1 overflow-y-auto p-3 bg-gray-50 dark:bg-[#0d1424]">
                {combinedChatList.filter(item => item.type === 'friend').map((item) => {
                  const isSelected = selectedChatsForDeletion.has(item.id);
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-[#24354f]/50 transition-colors cursor-pointer mb-2"
                      onClick={() => {
                        const newSet = new Set(selectedChatsForDeletion);
                        if (isSelected) {
                          newSet.delete(item.id);
                        } else {
                          newSet.add(item.id);
                        }
                        setSelectedChatsForDeletion(newSet);
                      }}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        isSelected 
                          ? 'bg-[#2563eb] border-[#3d8ef0]' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {isSelected && <CheckSquare className="w-3 h-3 text-white" />}
                      </div>
                      <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden">
                        {item.type === 'friend'
                          ? (item.data.avatarUrl ? (
                              <img src={item.data.avatarUrl} alt={getDisplayName(item.data)} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <div className="w-full h-full rounded-full bg-[#2563eb]/10 flex items-center justify-center text-sm font-semibold text-[#3d8ef0] dark:text-[#7ab8f8]">
                                {getInitials(getDisplayName(item.data))}
                              </div>
                            ))
                          : <GroupAvatar group={item.data} size="md" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">
                          {item.type === 'friend' ? (
                            <Username user={item.data} variant="default" />
                          ) : (
                            <Username user={item.data} variant="default" />
                          )}
                        </p>
                        {item.data.lastMessage && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {item.data.lastMessage.replace(/<[^>]*>/g, '').substring(0, 50)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            /* Chat List */
            <motion.div
              key="chats"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0"
            >
              <div 
                className="chat-scroll-area p-3 h-full overflow-y-auto"
                onContextMenu={(e) => {
                  // Only show context menu if clicking on empty area (not on a friend item)
                  if (e.target.closest('[data-friend-item]')) {
                    return; // Friend item has its own context menu
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  setEmptyAreaContextMenu({
                    isOpen: true,
                    x: e.clientX,
                    y: e.clientY,
                  });
                }}
              >
                <AnimatePresence>
                  {filteredChatList.length === 0 ? (
                    <div className="text-center text-gray-500 dark:text-gray-500 text-sm py-12">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[#1a2640] flex items-center justify-center">
                          <MessageCircle className="w-6 h-6 text-gray-400 dark:text-gray-400" />
                        </div>
                        <p>{searchQuery ? t('sidebar.emptyChats.noResults') : t('sidebar.emptyChats.none')}</p>
                        {!searchQuery && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('sidebar.emptyChats.hint')}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    filteredChatList.map((item, index) => {
                      // Handle friend items
                      if (item.type === 'friend') {
                        const friend = item.data;
                        const friendId = friend.email || friend.id;
                        const isSelected = selectedFriendId === friendId && !selectedGroupId;
                        const friendKey = friend.email || friend.id || `friend-${index}`;
                        const isDeleted = friend.isDeleted === true;
                      
                        return (
                        <motion.div
                          key={friendKey}
                          data-friend-item
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          className={`mb-2.5 p-3.5 rounded-xl cursor-pointer transition-all duration-200 group ${
                            isSelected
                              ? 'bg-[#2563eb]/10 dark:bg-[#1a2640] border-primary-300 dark:border-[#3d8ef0]/50 shadow-elevated'
                              : 'bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-white/5 border border-gray-200 dark:border-transparent hover:border-gray-300 dark:hover:border-white/10'
                          } ${isDeleted ? 'opacity-75 border-dashed' : ''}`}
                          onClick={() => {
                            const friendId = friend.email || friend.id;
                            if (friendId) {
                              selectFriend(friendId);
                            }
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setContextMenu({
                              isOpen: true,
                              x: e.clientX,
                              y: e.clientY,
                              friend: friend,
                            });
                            setSelectedFriendForAction(friend);
                          }}
                        >
                          <div className="flex items-start gap-3">
                            {/* Avatar */}
                            <div className="relative flex-shrink-0">
                              <div
                                className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold ${
                                  isSelected
                                    ? 'bg-white text-gray-900 dark:bg-white/10 dark:text-white'
                                    : 'bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white'
                                }`}
                              >
                                {friend.avatarUrl ? (
                                  <img
                                    src={friend.avatarUrl}
                                    alt={getDisplayName(friend)}
                                    className="w-full h-full rounded-full object-cover"
                                  />
                                ) : (
                                  getInitials(getDisplayName(friend))
                                )}
                              </div>
                              {/* Status Indicator - Hidden for deleted friends */}
                              {/* WhatsApp-like: green for online, gray for offline */}
                              {!isDeleted && (
                                <div
                                  className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 ${
                                    isSelected ? 'border-white' : 'border-white dark:border-white/10'
                                  } ${
                                    friend.status === 'online' || friend.is_online === true ? 'bg-green-500' : 'bg-gray-400 dark:bg-[#24354f]'
                                  }`}
                                />
                              )}
                            </div>

                            {/* Friend Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <h3
                                  className={`truncate text-sm leading-tight flex-1 min-w-0 ${
                                    isDeleted ? 'line-through opacity-60' : ''
                                  }`}
                                >
                                  <Username 
                                    user={friend} 
                                    variant="default" 
                                    className={isSelected && !isPlusMember(friend) ? 'text-[#3d8ef0] dark:text-primary-300' : ''}
                                  />
                                  {isDeleted && (
                                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                      {t('sidebar.deletedFriendTag')}
                                    </span>
                                  )}
                                </h3>
                                <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                                  <div className="flex items-center gap-2">
                                    {friend.unreadCount > 0 && (
                                      <span className={`text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center ${
                                        isSelected
                                          ? 'bg-white text-[#3d8ef0]'
                                          : 'bg-red-500 text-white'
                                      }`}>
                                        {friend.unreadCount > 99 ? '99+' : friend.unreadCount}
                                      </span>
                                    )}
                                    {friend.lastMessageTime && (
                                      <span className="text-xs text-gray-500 dark:text-gray-500">
                                        {formatMessageTime(friend.lastMessageTime)}
                                      </span>
                                    )}
                                  </div>
                                  {friend.pinned && (
                                    <div className="flex items-center justify-end">
                                      <Pin className="w-3 h-3 text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0" fill="currentColor" />
                                    </div>
                                  )}
                                </div>
                              </div>
                              {friend.lastMessage && (
                                <p
                                  className={`text-xs truncate ${
                                    isSelected
                                      ? 'text-[#3d8ef0]/80 dark:text-[#7ab8f8]/80'
                                      : 'text-gray-600 dark:text-gray-400'
                                  }`}
                                >
                                  <TwemojiText text={String(friend.lastMessage)} />
                                </p>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                      } else if (item.type === 'group') {
                        // Handle group items
                        const group = item.data;
                        const isSelected = selectedGroupId === group.id && !selectedFriendId;
                        const groupKey = group.id || `group-${index}`;
                        const groupInitials = group.name ? getInitials(group.name) : '?';
                        
                        return (
                          <motion.div
                            key={groupKey}
                            data-group-item
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.2 }}
                            className={`mb-2.5 p-3.5 rounded-xl cursor-pointer transition-all duration-200 group ${
                              isSelected
                                ? 'bg-[#2563eb]/10 dark:bg-[#1a2640] border-primary-300 dark:border-[#3d8ef0]/50 shadow-elevated'
                                : 'bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-white/5 border border-gray-200 dark:border-transparent hover:border-gray-300 dark:hover:border-white/10'
                            }`}
                            onClick={() => {
                              handleSelectGroup(group.id);
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setGroupContextMenu({
                                isOpen: true,
                                x: e.clientX,
                                y: e.clientY,
                                group: group,
                              });
                              setSelectedGroupForAction(group);
                            }}
                          >
                            <div className="flex items-center gap-3">
                              {/* Avatar */}
                              <div className="relative flex-shrink-0">
                                <GroupAvatar group={group} size="lg" />
                                {/* Plus badge if Plus group */}
                                {group.is_plus_group && (
                                  <div className="plus-badge-prestige absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-yellow-600 dark:border-yellow-500">
                                    <Star className="w-3 h-3 text-black fill-black" />
                                  </div>
                                )}
                                {/* Group indicator icon */}
                                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center border-2 border-white dark:border-white/10">
                                  <UsersRound className="w-2.5 h-2.5 text-white" />
                                </div>
                              </div>
                              
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                                      {group.name}
                                    </h3>
                                    {group.muted && (
                                      <BellOff className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    )}
                                  </div>
                                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                                    <div className="flex items-center gap-2">
                                      {group.unreadCount > 0 && (
                                        <span className={`text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center ${
                                          isSelected
                                            ? 'bg-white text-[#3d8ef0]'
                                            : 'bg-red-500 text-white'
                                        }`}>
                                          {group.unreadCount > 99 ? '99+' : group.unreadCount}
                                        </span>
                                      )}
                                      {group.lastMessageTime && (
                                        <span className="text-xs text-gray-500 dark:text-gray-500">
                                          {formatMessageTime(group.lastMessageTime)}
                                        </span>
                                      )}
                                    </div>
                                    {group.pinned && (
                                      <div className="flex items-center justify-end">
                                        <Pin className="w-3 h-3 text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0" fill="currentColor" />
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {group.lastMessage && (
                                  <p
                                    className={`text-xs truncate ${
                                      isSelected
                                        ? 'text-[#3d8ef0]/80 dark:text-[#7ab8f8]/80'
                                        : 'text-gray-600 dark:text-gray-400'
                                    }`}
                                  >
                                    {(() => {
                                      let msgText = '';
                                      if (typeof group.lastMessage === 'string') {
                                        msgText = group.lastMessage;
                                      } else if (group.lastMessage && typeof group.lastMessage === 'object') {
                                        msgText = String(group.lastMessage.content || group.lastMessage.file_name || t('sidebar.defaultMessage'));
                                      } else {
                                        msgText = t('sidebar.defaultMessage');
                                      }
                                      const text = msgText.length > 30 ? msgText.substring(0, 30) + '...' : msgText;
                                      return <TwemojiText text={text} />;
                                    })()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      }
                      return null;
                    })
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Add Friend Modal */}
      <AddFriendModal
        isOpen={showAddFriendModal}
        onClose={() => setShowAddFriendModal(false)}
      />

      {/* Create Group Modal */}
      <CreateGroupModal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
      />

      {/* Friends List Modal - Friend Requests */}
      <FriendsListModal
        isOpen={showFriendsListModal}
        onClose={() => setShowFriendsListModal(false)}
      />

      {/* Empty Area Context Menu */}
      <ContextMenu
        isOpen={emptyAreaContextMenu.isOpen}
        position={{ x: emptyAreaContextMenu.x, y: emptyAreaContextMenu.y }}
        items={[
          {
            label: t('sidebar.contextMenu.newMessage'),
            icon: MessageSquarePlus,
            onClick: () => {
              setShowFriendsListModal(true);
            },
          },
          {
            label: t('sidebar.contextMenu.newFriend'),
            icon: Plus,
            onClick: () => {
              setShowAddFriendModal(true);
            },
          },
        ]}
        onClose={() => setEmptyAreaContextMenu({ isOpen: false, x: 0, y: 0 })}
      />

      {/* Friend Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={{ x: contextMenu.x, y: contextMenu.y }}
        items={[
          ...(contextMenu.friend?.isDeleted !== true ? [
            {
              label: contextMenu.friend?.pinned ? t('sidebar.contextMenu.unpin') : t('sidebar.contextMenu.pin'),
              icon: Pin,
              onClick: async () => {
                if (contextMenu.friend) {
                  const friendId = contextMenu.friend.email || contextMenu.friend.id;
                  if (friendId) {
                    await pinFriend(friendId, !contextMenu.friend.pinned);
                  }
                }
              },
            },
            {
              label: isFriendMuted(contextMenu.friend) ? t('sidebar.contextMenu.unmute') : t('sidebar.contextMenu.mute'),
              icon: isFriendMuted(contextMenu.friend) ? Bell : BellOff,
              onClick: () => {
                if (contextMenu.friend) {
                  setSelectedFriendForAction(contextMenu.friend);
                  setShowMuteModal(true);
                }
              },
            },
            {
              label: t('sidebar.contextMenu.addNickname'),
              icon: User,
              onClick: () => {
                if (contextMenu.friend) {
                  setSelectedFriendForAction(contextMenu.friend);
                  setShowNicknameModal(true);
                }
              },
            },
          ] : []),
          {
            label: t('sidebar.contextMenu.deleteMessages'),
            icon: Trash2,
            onClick: () => {
              if (contextMenu.friend) {
                setShowDeleteMessagesModal(true);
              }
            },
          },
          ...(contextMenu.friend?.isDeleted !== true ? [
            {
              label: t('sidebar.contextMenu.deleteFriend'),
              icon: UserX,
              danger: true,
              onClick: () => {
                if (contextMenu.friend) {
                  setShowDeleteFriendModal(true);
                }
              },
            },
          ] : []),
        ]}
        onClose={() => setContextMenu({ isOpen: false, x: 0, y: 0, friend: null })}
      />

      {/* Group Context Menu */}
      <ContextMenu
        isOpen={groupContextMenu.isOpen}
        position={{ x: groupContextMenu.x, y: groupContextMenu.y }}
        items={
          // If group is destroyed, show ONLY "Kaldır" option
          groupContextMenu.group?.is_active === false ? (
            [
              {
                label: t('sidebar.groupContextMenu.remove'),
                icon: Trash2,
                onClick: handleRemoveGroup,
                danger: true,
              },
            ]
          ) : (
            // Normal group menu
            [
              {
                label: groupContextMenu.group?.muted ? t('sidebar.contextMenu.unmute') : t('sidebar.contextMenu.mute'),
                icon: groupContextMenu.group?.muted ? Bell : BellOff,
                onClick: handleGroupMute,
              },
              {
                label: groupContextMenu.group?.pinned ? t('sidebar.contextMenu.unpin') : t('sidebar.contextMenu.pin'),
                icon: Pin,
                onClick: async () => {
                  const group = groupContextMenu.group;
                  if (group?.id && pinGroup) {
                    console.log('📌 [ChatSidebar] Pin group clicked', { groupId: group.id, currentPinned: group.pinned });
                    const success = await pinGroup(group.id, !group.pinned);
                    console.log('📌 [ChatSidebar] Pin group result', { success });
                    if (success) {
                      // Reload groups to reflect the change
                      if (loadGroups) {
                        await loadGroups(true);
                      }
                    } else {
                      // Show error toast
                      window.dispatchEvent(
                        new CustomEvent('notesplus-show-toast', {
                          detail: {
                            type: 'error',
                            title: t('sidebar.groupContextMenu.pinErrorTitle'),
                            message: t('sidebar.groupContextMenu.pinErrorMessage'),
                          },
                        })
                      );
                    }
                  } else {
                    console.warn('⚠️ [ChatSidebar] Pin group: Missing group or pinGroup function', { group, pinGroup: !!pinGroup });
                  }
                  setGroupContextMenu({ isOpen: false, x: 0, y: 0, group: null });
                },
              },
              {
                label: t('sidebar.groupContextMenu.clearMessages'),
                icon: Trash2,
                onClick: handleClearMessages,
              },
              // Kurucu ise: Grubu Boz
              // Normal üye/admin ise: Gruptan Ayrıl
              ...(groupContextMenu.group?.role === 'owner' ? (
                [
                  {
                    label: t('sidebar.groupContextMenu.destroyGroup'),
                    icon: Trash2,
                    onClick: handleDestroyGroup,
                    danger: true,
                  },
                ]
              ) : (
                [
                  {
                    label: t('sidebar.groupContextMenu.leaveGroup'),
                    icon: UserX,
                    onClick: handleGroupLeave,
                    danger: true,
                  },
                ]
              )),
            ]
          )
        }
        onClose={() => setGroupContextMenu({ isOpen: false, x: 0, y: 0, group: null })}
      />

      {/* Mute Modal */}
      <MuteFriendModal
        isOpen={showMuteModal}
        onClose={() => {
          setShowMuteModal(false);
          setSelectedFriendForAction(null);
        }}
        onConfirm={async (mutedUntil) => {
          if (selectedFriendForAction) {
            const friendId = selectedFriendForAction.email || selectedFriendForAction.id;
            if (friendId) {
              await muteFriend(friendId, mutedUntil);
            }
          }
        }}
        friendName={selectedFriendForAction ? getDisplayName(selectedFriendForAction) : ''}
        isMuted={selectedFriendForAction ? isFriendMuted(selectedFriendForAction) : false}
      />

      {/* Nickname Modal */}
      <NicknameModal
        isOpen={showNicknameModal}
        onClose={() => {
          setShowNicknameModal(false);
          setSelectedFriendForAction(null);
        }}
        onConfirm={async (nickname) => {
          if (selectedFriendForAction) {
            const friendId = selectedFriendForAction.email || selectedFriendForAction.id;
            if (friendId) {
              await setNickname(friendId, nickname);
            }
          }
        }}
        friendName={selectedFriendForAction ? getDisplayName(selectedFriendForAction) : ''}
        currentNickname={selectedFriendForAction?.nickname || null}
      />

      {/* Bulk Delete Confirm Modal */}
      <DeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          for (const chatId of selectedChatsForDeletion) {
            const item = combinedChatList.find(c => c.id === chatId);
            if (item) {
              if (item.type === 'friend') {
                await deleteMessages(item.id);
              } else if (item.type === 'group') {
                await deleteGroupMessages(item.id);
              }
            }
          }
          setShowDeleteConfirm(false);
          setSelectChatsMode(false);
          setSelectedChatsForDeletion(new Set());
        }}
        title={t('sidebar.modals.deleteChatsTitle')}
        message={t('sidebar.modals.deleteChatsMessage', { count: selectedChatsForDeletion.size })}
      />

      {/* Delete Messages Modal */}
      <DeleteModal
        isOpen={showDeleteMessagesModal}
        onClose={() => {
          setShowDeleteMessagesModal(false);
          setSelectedFriendForAction(null);
        }}
        onConfirm={async () => {
          if (selectedFriendForAction) {
            const friendId = selectedFriendForAction.email || selectedFriendForAction.id;
            if (friendId) {
              await deleteMessages(friendId);
            }
          }
          setShowDeleteMessagesModal(false);
          setSelectedFriendForAction(null);
        }}
        title={t('sidebar.modals.deleteMessagesTitle')}
        message={t('sidebar.modals.deleteMessagesMessage', { name: selectedFriendForAction ? getDisplayName(selectedFriendForAction) : t('sidebar.modals.deleteMessagesDefaultName') })}
      />

      {/* Delete Group Modal (Leave Group) */}
      <DeleteModal
        isOpen={showDeleteGroupModal}
        onClose={() => {
          setShowDeleteGroupModal(false);
          setSelectedGroupForAction(null);
        }}
        onConfirm={handleConfirmLeaveGroup}
        title={t('sidebar.modals.leaveGroupTitle')}
        message={selectedGroupForAction ? t('sidebar.modals.leaveGroupMessage', { name: selectedGroupForAction.name }) : ''}
      />

      {/* Destroy Group Modal */}
      <DeleteModal
        isOpen={showDestroyGroupModal}
        onClose={() => {
          setShowDestroyGroupModal(false);
          setSelectedGroupForAction(null);
        }}
        onConfirm={handleConfirmDestroyGroup}
        title={t('sidebar.modals.destroyGroupTitle')}
        message={selectedGroupForAction ? t('sidebar.modals.destroyGroupMessage', { name: selectedGroupForAction.name }) : ''}
      />

      {/* Clear Messages Modal */}
      <DeleteModal
        isOpen={showClearMessagesModal}
        onClose={() => {
          setShowClearMessagesModal(false);
          setSelectedGroupForAction(null);
        }}
        onConfirm={handleConfirmClearMessages}
        title={t('sidebar.modals.clearMessagesTitle')}
        message={selectedGroupForAction ? t('sidebar.modals.clearMessagesMessage', { name: selectedGroupForAction.name }) : ''}
      />

      {/* Delete Group Messages Modal */}
      <DeleteModal
        isOpen={showDeleteGroupMessagesModal}
        onClose={() => {
          setShowDeleteGroupMessagesModal(false);
          setSelectedGroupForAction(null);
        }}
        onConfirm={handleConfirmDeleteMessages}
        title={t('sidebar.modals.deleteGroupMessagesTitle')}
        message={selectedGroupForAction ? t('sidebar.modals.deleteGroupMessagesMessage', { name: selectedGroupForAction.name }) : ''}
      />


      {/* Delete Friend Modal */}
      <DeleteModal
        isOpen={showDeleteFriendModal}
        onClose={() => {
          setShowDeleteFriendModal(false);
          setSelectedFriendForAction(null);
        }}
        onConfirm={async () => {
          if (selectedFriendForAction) {
            const friendId = selectedFriendForAction.email || selectedFriendForAction.id;
            if (friendId) {
              await deleteFriend(friendId);
            }
          }
          setShowDeleteFriendModal(false);
          setSelectedFriendForAction(null);
        }}
        title={t('sidebar.modals.deleteFriendTitle')}
        message={t('sidebar.modals.deleteFriendMessage', { name: selectedFriendForAction ? getDisplayName(selectedFriendForAction) : t('sidebar.modals.deleteFriendDefaultName') })}
      />

      <AvatarCropModal
        isOpen={showAvatarCropModal}
        onClose={() => setShowAvatarCropModal(false)}
        imageFile={selectedAvatarFile}
        onSave={handleAvatarSave}
        title={t('sidebar.avatar.cropTitle')}
      />
    </div>
  );
};

export default ChatSidebar;

